/**
 * `<CandidateIdentityEditor>` — minimal inline editor for a candidate's
 * identity + contact fields (name, LinkedIn URL, phone, email).
 *
 * WHY: an upload whose name can't be parsed is saved as literally "Untitled
 * candidate" with no way to rename it, and misparsed / missing contact info
 * had no fix-it path either. This closes that trap with the smallest possible
 * affordance — four text inputs saved via the existing `useUpdateCandidate`
 * PUT (which invalidates the candidate query).
 *
 * EDIT GATE (mirrors `NoteRow`): the OWNING recruiter
 * (candidate `created_by` === current user) OR a tenant admin (admin / owner /
 * super_admin) sees the affordance. Everyone else gets nothing — this
 * component renders `null`, so the read-only sidebar is byte-identical to the
 * pre-change baseline for a non-owner non-admin viewer (visual snapshots run as
 * such a member, so no baseline regeneration is needed). This is a COSMETIC
 * gate; the backend PUT is the source of truth (a recruiter editing someone
 * else's candidate gets a 404/403 server-side).
 *
 * Scope is deliberately narrow: no photo, no résumé re-upload, no status
 * (status has its own control). Just the 4 identity/contact fields.
 */
import { useState, type JSX } from "react";

import { Button } from "@/components/common/Button";
import { useAuthStore } from "@/stores/useAuthStore";
import {
  useUpdateCandidate,
  type CandidateUpdateInput,
} from "@/hooks/queries/candidates/useUpdateCandidate";

/** Admin/owner/super-admin may edit any candidate (mirrors NoteRow). */
function isAdminLike(role: string | null): boolean {
  return role === "admin" || role === "owner" || role === "super_admin";
}

interface CandidateIdentityEditorProps {
  candidateId: string;
  name: string;
  linkedinUrl: string | null | undefined;
  email: string | null | undefined;
  phone: string | null | undefined;
  /** Owning recruiter's user id (candidate `created_by`) — owner half of the gate. */
  ownerUserId?: string | null;
}

const INPUT_CLS =
  "w-full rounded-md border border-border-default bg-surface-subtle px-2 py-1 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-border-focus focus-visible:shadow-focus disabled:opacity-60 disabled:cursor-not-allowed";

interface Draft {
  name: string;
  linkedin_url: string;
  phone: string;
  email: string;
}

function EditField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-2xs text-text-tertiary">
        {label}
      </label>
      {children}
    </div>
  );
}

function IdentityEditForm({
  candidateId,
  initial,
  onClose,
}: {
  candidateId: string;
  initial: Draft;
  onClose: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<Draft>(initial);
  const updateCandidate = useUpdateCandidate();

  const set =
    (key: keyof Draft) =>
    (e: { target: { value: string } }): void =>
      setDraft((d) => ({ ...d, [key]: e.target.value }));

  const nameTrimmed = draft.name.trim();
  const nameValid = nameTrimmed !== "";

  // Only send fields the user actually changed (PATCH-like). Trailing/leading
  // whitespace is trimmed by the backend; we compare trimmed values so a pure
  // whitespace edit isn't sent as a change.
  function buildInput(): CandidateUpdateInput {
    const body: CandidateUpdateInput = { id: candidateId };
    if (nameTrimmed !== initial.name.trim()) body.name = nameTrimmed;
    if (draft.linkedin_url.trim() !== initial.linkedin_url.trim())
      body.linkedin_url = draft.linkedin_url.trim() || null;
    if (draft.phone.trim() !== initial.phone.trim())
      body.phone = draft.phone.trim() || null;
    if (draft.email.trim() !== initial.email.trim())
      body.email = draft.email.trim() || null;
    return body;
  }

  const input = buildInput();
  // Number of changed fields (everything except the always-present id).
  const hasChanges = Object.keys(input).length > 1;

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!nameValid || !hasChanges) return;
    updateCandidate.mutate(input, { onSuccess: () => onClose() });
  };

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={onSubmit}
      aria-label="Edit candidate details"
      data-testid="candidate-identity-edit-form"
    >
      <EditField label="Name" htmlFor="ci-name">
        <input
          id="ci-name"
          type="text"
          className={INPUT_CLS}
          value={draft.name}
          onChange={set("name")}
          disabled={updateCandidate.isPending}
          aria-invalid={!nameValid}
          data-testid="candidate-identity-name"
        />
      </EditField>
      <EditField label="LinkedIn URL" htmlFor="ci-linkedin">
        <input
          id="ci-linkedin"
          type="text"
          className={INPUT_CLS}
          value={draft.linkedin_url}
          onChange={set("linkedin_url")}
          disabled={updateCandidate.isPending}
          data-testid="candidate-identity-linkedin"
        />
      </EditField>
      <EditField label="Phone" htmlFor="ci-phone">
        <input
          id="ci-phone"
          type="text"
          className={INPUT_CLS}
          value={draft.phone}
          onChange={set("phone")}
          disabled={updateCandidate.isPending}
          data-testid="candidate-identity-phone"
        />
      </EditField>
      <EditField label="Email" htmlFor="ci-email">
        <input
          id="ci-email"
          type="text"
          className={INPUT_CLS}
          value={draft.email}
          onChange={set("email")}
          disabled={updateCandidate.isPending}
          data-testid="candidate-identity-email"
        />
      </EditField>

      {!nameValid ? (
        <p
          className="text-2xs text-status-rejected-fg"
          data-testid="candidate-identity-name-error"
        >
          Name can't be empty.
        </p>
      ) : null}

      {updateCandidate.isError ? (
        <p
          role="alert"
          className="text-2xs text-status-rejected-fg"
          data-testid="candidate-identity-error"
        >
          Couldn't save. Please try again.
        </p>
      ) : null}

      <div className="flex items-center justify-end gap-2 pt-0.5">
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          onClick={onClose}
          disabled={updateCandidate.isPending}
          data-testid="candidate-identity-cancel"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={updateCandidate.isPending || !nameValid || !hasChanges}
          data-testid="candidate-identity-save"
        >
          {updateCandidate.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

export function CandidateIdentityEditor({
  candidateId,
  name,
  linkedinUrl,
  email,
  phone,
  ownerUserId,
}: CandidateIdentityEditorProps): JSX.Element | null {
  const [isEditing, setIsEditing] = useState(false);

  // Edit gate — owning recruiter OR tenant admin (cosmetic; backend enforces).
  const currentUserId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const isOwner =
    currentUserId != null &&
    currentUserId !== "" &&
    ownerUserId != null &&
    ownerUserId === currentUserId;
  const canEdit = isOwner || isAdminLike(role);

  // Non-editors get nothing extra — the read-only sidebar is unchanged.
  if (!canEdit) return null;

  const initial: Draft = {
    name,
    linkedin_url: linkedinUrl ?? "",
    phone: phone ?? "",
    email: email ?? "",
  };

  return (
    <div className="flex flex-col gap-2" data-testid="candidate-identity-editor">
      {isEditing ? (
        // `key` remounts the form so it re-seeds from the latest candidate
        // values each time the user opens it.
        <IdentityEditForm
          key={`${name}|${linkedinUrl ?? ""}|${phone ?? ""}|${email ?? ""}`}
          candidateId={candidateId}
          initial={initial}
          onClose={() => setIsEditing(false)}
        />
      ) : (
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          className="self-start"
          onClick={() => setIsEditing(true)}
          data-testid="candidate-identity-edit-btn"
        >
          Edit details
        </Button>
      )}
    </div>
  );
}
